import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { CHAPTERS, CHAPTER_COUNT, TOTAL_SERVICES } from './storyData';
import { buildSchedule, publishArmSync, clearArmSync, RISE, RISE_LEAD } from './beats';
import { buildLayout, DEPTH } from './layout';
import { mixHex, ramp } from './palette';
import StageVisual from './StageVisual';
import { WorldBack } from './SectorWorld';
import MobileStory from './MobileStory';
import { INDUSTRY_ICONS } from '../../data/industriesData';
import { ServiceIcon } from '../../data/serviceIcons';
import './ServicesStory.css';

gsap.registerPlugin(ScrollTrigger);

/* ═══════════════════════════════════════════════════════════════════════════
   THE STORY

   One board. Four sectors. Every service it powers.

   The homepage has already told the first two acts by the time the reader
   arrives here: the board is built in the hero, and it flies across the page
   to a robot cell. This section is act three, and it is a pick-and-place loop
   — four of them, one per sector:

     APPROACH  the arm carries the board in. The bay it will be set into wakes
               up, and the sector rises into frame behind it.
     SEAT      CONTACT. The bay flares under the board and the sector's
               core boots out of the thing it is standing on.
     CHARGE    power leaves the board along the feed and reaches the site.
     RISE      the services fly THROUGH the frame. Each is the real render of
               the thing it is — the neonatal warmer, the generator set with
               its dashboard — and it comes up from below the floor, settles at
               hero position under its own light, holds, then keeps going up
               and out while the next is already rising behind it. Straight out
               of the reference's `scene3`, where every hill runs from y +300 to
               y -550 and nothing ever stops.
     HOLD      the finished sector picture sits still and is read.
     LIFT      the arm closes on the board again. The services retract down
               their own branches, the bus goes dark, the core folds back into
               the bay, and the whole sector falls past the camera while the
               next one rises through it.

   ── why it is built this way ──

   ONE master timeline drives all four sectors, with everything placed at
   absolute times. Separate ScrollTriggers per sector would need coordinating
   at the seams; a single scrubbed timeline cannot develop a gap or an overlap
   it was not told to have.

   The clock lives in `beats.js`, in ABSOLUTE vh rather than fractions of a
   sector's span — because the robot arm has to meet this timeline, and it is
   a separate WebGL overlay with its own scroll mapping. A sector's beats are
   published to it as scroll fractions (`publishArmSync`) and it maps them onto
   page pixels off the same section box it was already measuring. That is the
   entire contract between the two, and it runs one way.

   ── why there is no packet any more ──

   v1 ran a dot along a bezier and dropped an icon at each tap. Nothing in the
   reference works that way, and the reason matters: every moving object in
   that scene is a THING — a hill, a tree, a bird — and the view changes
   because the camera travels PAST those things. Abstract markers sliding
   along a guide do not accumulate into a place, however well they are eased.

   So a service is not a dot here. It is somewhere you arrive at, and the view
   is different at each one because the camera has moved.

   What stands there is the real thing. `public/visuals/sector-services/` already
   held a purpose-made render per service, numbered in service order, and those
   are what the stage shows now (see serviceVisuals.js — the section loads
   trimmed WebP derivatives, 2.5 MB for the set rather than 35 MB). The
   environment around them is a studio rather than a place, because that is what
   product renders belong in: a cove, a floor, a pool of light. Drawing little
   cartoon buildings next to a photographic render made the render look pasted
   on, and the drawing is what the eye judged the frame by. See StudioSet.jsx.

   ── the camera ──

   There are two of them, at right angles. Between SECTORS it travels UP: the
   outgoing sector flies past the lens while it grows and the next rises from
   below. Within a sector it travels SIDEWAYS along the site, one installation
   at a time. Both are the same idea — near things move a long way, far things
   barely move — and keeping each of them to a single consistent direction is
   what stops either reading as slides being shuffled.

   The other half of the redesign is that there IS one, and that it only ever
   goes one way: UP. An outgoing sector flies up and past the lens while it
   grows; the incoming sector rises into frame from below; every band of the
   world travels up at a rate set by how far away it is. One direction, four
   speeds — that is the whole of parallax, and getting the direction consistent
   is what stops a hand-off reading as two slides being shuffled.

   The travel is large on purpose. A sector rises from 470 to 620 units below
   the frame and leaves 540 above it, and the nearest band of the world crosses
   the entire height on every hand-off. Layers that only shift by a few percent
   are wallpaper; the depth comes from near things moving a long way while far
   things barely move at all.

   `SectorWorld` is that world — sky, far skyline, motes, near board edges —
   and its sky is not a backdrop but a beat: the gradient's own stops are
   tweened per sector, so the light in the room is somewhere different by the
   time the reader reaches Education than it was at Healthcare.
   ═══════════════════════════════════════════════════════════════════════════ */

// Below this the stage is switched out for the mobile telling. It is wider than
// a typical phone breakpoint on purpose: the horizontal bus has to fit up to six
// service nodes *and* the copy panel, and under ~1100px their labels start
// running into each other. The mobile version is the better experience there
// anyway - it is not a fallback.
const MOBILE_QUERY = '(max-width: 1100px)';

/* How present the sector's place is. Nearly full: it is the establishing shot
   and the reader is meant to SEE that this is a hospital. What keeps it behind
   the service render is that it is dimmed slightly, desaturated slightly, and
   drifts slower than anything else — not that it has been faded away. */
const PLACE_ALPHA = 0.9;

/* How far a render lifts as it settles onto its mark, in stage pixels. Small:
   the travel is the CAMERA's, and the object only has to arrive, not fly. */
const LIFT_IN = 26;

const clamp01 = v => (v < 0 ? 0 : v > 1 ? 1 : v);
const invLerp = (a, b, v) => (b === a ? 0 : clamp01((v - a) / (b - a)));

export default function ServicesStory() {
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const railRef = useRef(null);

  const [isMobile, setIsMobile] = useState(
    () => (typeof window !== 'undefined' ? window.matchMedia(MOBILE_QUERY).matches : false)
  );
  const [reduced, setReduced] = useState(
    () => (typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false)
  );
  /* The trace canvas's own box, AND where it sits inside the pinned stage.
     The background is full-bleed across the whole stage but its horizon has
     to land exactly on the floor line the renders stand on, and that line is
     measured inside the canvas — so the offset between the two boxes is not
     optional detail, it is what keeps the horizon and the floor the same
     line at every viewport. */
  const [dims, setDims] = useState({ w: 0, h: 0, top: 0, stageW: 0, stageH: 0 });
  /* `phase` is what the sector is doing, and it has four values, not three —
     see the note on the readout. `seated` is kept as its own field because the
     stage's data attributes key off it directly. */
  const [active, setActive] = useState({ chapter: 0, service: -1, seated: false, phase: 'inbound' });

  /* ── Media queries ─────────────────────────────────────────────────────── */
  useEffect(() => {
    const mqMobile = window.matchMedia(MOBILE_QUERY);
    const mqMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onMobile = e => setIsMobile(e.matches);
    const onMotion = e => setReduced(e.matches);
    mqMobile.addEventListener('change', onMobile);
    mqMotion.addEventListener('change', onMotion);
    return () => {
      mqMobile.removeEventListener('change', onMobile);
      mqMotion.removeEventListener('change', onMotion);
    };
  }, []);

  /* ── Measure the canvas ────────────────────────────────────────────────────
     Geometry is rebuilt from real measurements rather than assumed viewport
     maths, and only when the box actually changes by enough to matter — a
     sub-pixel ResizeObserver storm would otherwise rebuild the whole timeline
     on every frame of a window drag. */
  useLayoutEffect(() => {
    if (isMobile) return undefined;
    const el = canvasRef.current;
    if (!el) return undefined;

    let raf = null;
    const measure = () => {
      raf = null;
      const stage = stageRef.current;
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w < 2 || h < 2 || !stage) return;
      const cr = el.getBoundingClientRect();
      const sr = stage.getBoundingClientRect();
      const next = {
        w, h,
        top: Math.round(cr.top - sr.top),
        stageW: Math.round(sr.width),
        stageH: Math.round(sr.height),
      };
      setDims(prev => (
        Math.abs(prev.w - next.w) > 6 || Math.abs(prev.h - next.h) > 6
          || Math.abs(prev.top - next.top) > 6 || Math.abs(prev.stageH - next.stageH) > 6
          || Math.abs(prev.stageW - next.stageW) > 6
          ? next : prev
      ));
    };

    measure();
    const ro = new ResizeObserver(() => {
      if (raf == null) raf = requestAnimationFrame(measure);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (raf != null) cancelAnimationFrame(raf);
    };
  }, [isMobile]);

  /* ── Geometry ──────────────────────────────────────────────────────────── */
  const layouts = useMemo(() => {
    if (!dims.w || !dims.h) return [];
    return CHAPTERS.map(ch => buildLayout({
      width: dims.w,
      height: dims.h,
      count: ch.services.length,
    }));
  }, [dims.w, dims.h]);

  /* ── The clock, shared by the timeline, the section height and the arm ─── */
  const schedule = useMemo(() => buildSchedule(CHAPTERS), []);
  const { marks, starts, total } = schedule;

  /* ── Tell the arm when to deliver ──────────────────────────────────────────
     The schedule is published from inside the story's own ScrollTrigger (see
     `onRefresh` below), because the trigger is the only thing that knows where
     this section actually begins and ends in scroll. All this effect does is
     withdraw the schedule when the desktop story is not on screen, so
     PcbJourney falls back to the single delivery it does without this section
     rather than waiting for cues that are not coming. */
  useLayoutEffect(() => {
    if (isMobile) clearArmSync();
    return () => { clearArmSync(); };
  }, [isMobile]);

  /* ── The timeline ──────────────────────────────────────────────────────── */
  useLayoutEffect(() => {
    if (isMobile || !layouts.length) return undefined;
    const root = rootRef.current;
    const stage = stageRef.current;
    const rail = railRef.current;
    if (!root || !stage || !rail) return undefined;

    const ctx = gsap.context(() => {
      const chapterEls = gsap.utils.toArray('.zx-chapter', root);
      if (chapterEls.length !== CHAPTERS.length) return;

      // This sector's scenery, and the lights in it.
      const sceneOf = id => gsap.utils.toArray(`[data-scene="${id}"]`, root);
      const lampsOf = id => gsap.utils.toArray(`[data-scene="${id}"] [data-lamp]`, root);

      const bundles = chapterEls.map((el, c) => ({
        el,
        trace: layouts[c],
        depth: el.querySelector('[data-depth]'),
        // The world's scenery for this sector, and the windows in it that come
        // on as its services land.
        scene: sceneOf(CHAPTERS[c].id),
        lamps: lampsOf(CHAPTERS[c].id),
        // The three distances a sector arrives from. Named for what they are
        // rather than for the elements they happen to be, because the rise
        // reads as depth and not as three fades.
        lBay: el.querySelector('[data-l="bay"]'),
        lNet: el.querySelector('[data-l="site"]'),
        lWork: el.querySelector('[data-l="work"]'),
        /* The strips the camera travels. `mid` is two elements — the SVG
           light-and-shadow and the HTML renders — which must move as one or a
           render drifts off its own pool of light. */
        deep: gsap.utils.toArray('[data-strip="deep"]', el),
        far: gsap.utils.toArray('[data-strip="far"]', el),
        mid: gsap.utils.toArray('[data-strip="mid"]', el),
        near: gsap.utils.toArray('[data-strip="near"]', el),
        // The light-and-shadow group in the SVG, and the render that stands
        // in it. Two elements per service, always animated together — they are
        // one object as far as the story is concerned.
        stations: gsap.utils.toArray('[data-station]', el),
        caps: gsap.utils.toArray('[data-cap]', el),
        core: el.querySelector('[data-core]'),
        halo: el.querySelector('[data-halo]'),
        pool: el.querySelector('[data-bay-pool]'),
        cross: el.querySelector('[data-bay-cross]'),
        socket: el.querySelector('[data-bay-socket]'),
        shock: el.querySelector('[data-bay-shock]'),
        feed: el.querySelector('[data-feed]'),
        plug: el.querySelector('[data-feed-plug]'),
      }));

      // The world. Each band is drift-wrapper + surge-inner; see SectorWorld.
      const W = name => ({
        drift: root.querySelector(`[data-drift="${name}"]`),
        surge: root.querySelector(`[data-surge="${name}"]`),
      });
      // Only the air is left out here; the ground is the site's own business.
      const world = { motes: W('motes') };
      /* Every driven surface of the background, in one place. The sky, the
         light on the horizon, the three ridges and the floor all carry the
         sector's colour, and all of them cross to the next sector together —
         repainting only the sky would leave a warm horizon over a floor that
         still belonged to the sector before it. */
      const bg = {
        sky: gsap.utils.toArray('[data-sky-stop]', root),
        glow: gsap.utils.toArray('[data-glow-stop]', root),
        ground: gsap.utils.toArray('[data-ground-stop]', root),
        bands: ['far', 'mid', 'near'].map(k => root.querySelector(`[data-band="${k}"]`)),
        sun: root.querySelector('[data-glow]'),
        // The sector's own place — the hospital, the grid, the plant, the
        // campus. One per sector, all mounted, one raised at a time.
      };

      // Story time, in the schedule's own vh units. Driven by the clock tween
      // built at the end of this function; see the note there.
      const clock = { t: 0 };

      // Where each service becomes the one the story is telling. Filled while
      // the timeline is built, so the scroll readout can never drift from the
      // animation it is describing.
      const landings = CHAPTERS.map(() => []);
      const lastState = { chapter: -1, service: -2, seated: null, phase: null };

      const applyActive = (chapter, service, seated) => {
        bundles.forEach((b, bc) => {
          const live = bc === chapter;
          b.el.dataset.settled = live && seated ? 'true' : 'false';
          b.el.dataset.seated = live && seated ? 'true' : 'false';
          /* Only the installation the camera is actually at is lit and
             reachable. v1 left every delivered service lit at once, which was
             honest for a bus diagram and wrong for a place: you are standing
             in front of ONE of these. */
          b.caps.forEach((cap, i) => {
            const here = live && seated && i === service;
            cap.dataset.arrived = here ? 'true' : 'false';
            cap.tabIndex = here ? 0 : -1;
          });
          b.stations.forEach((st, i) => {
            const here = live && seated && i === service;
            st.dataset.lit = here ? 'true' : 'false';
          });
        });
      };

      const chapterAt = time => {
        let c = 0;
        while (c < CHAPTER_COUNT - 1 && time >= starts[c + 1]) c++;
        return c;
      };

      /* The room's colour at a moment in the story: the live sector's accent,
         crossing to the next one across that sector's exit. */
      const accentAt = time => {
        const c = chapterAt(time);
        const next = Math.min(CHAPTER_COUNT - 1, c + 1);
        if (next === c) return CHAPTERS[c].accent;
        const M = marks[c];
        const k = invLerp(starts[c] + M.liftAt, starts[c] + M.span, time);
        return mixHex(CHAPTERS[c].accent, CHAPTERS[next].accent, k);
      };

      /* Two clocks, two owners.

         `--zx-fill` is a scroll indicator and belongs to the SCROLL: it should
         reach the end of its track when the reader reaches the end of the
         section, not a moment later.

         `--zx-live-accent`, and everything else that describes what is being
         shown, belongs to the TIMELINE, which lags the scroll by design — that
         lag is the glide. Anything in this group that is driven by scroll
         instead announces a sector before it has arrived. */
      const paintFill = progress => root.style.setProperty('--zx-fill', String(progress));
      const paintAccent = time => root.style.setProperty('--zx-live-accent', accentAt(time));

      /* ── Initial state ─────────────────────────────────────────────────────
         Every animated property is planted explicitly here rather than left to
         a fromTo's immediateRender. Combined with `immediateRender: false` on
         the timeline, that means the resting state is stated once, in one
         place, instead of emerging from whichever tween happened to be built
         last — which is what makes scrubbing backwards reliable. */
      bundles.forEach(b => {
        const T = b.trace;
        const bayOrigin = `${T.bay.x} ${T.bay.y}`;

        gsap.set(b.el, { autoAlpha: 0 });
        gsap.set(b.depth, {
          y: 0,
          scale: 1,
          // A sector passes the lens from its own core, not from the middle of
          // a box that has nothing at its middle.
          transformOrigin: `${T.core.x}px ${T.core.y}px`,
        });
        gsap.set([b.lBay, b.lNet, b.lWork], { y: 0, scale: 1 });
        gsap.set(b.core, {
          xPercent: -50, yPercent: -50,
          // Grows out of the bay it is standing on: the origin is the bay's
          // line through the core's own box, not the core's centre.
          transformOrigin: '50% 81%',
          scaleX: 0.55, scaleY: 0.06, y: 20,
          autoAlpha: 0, filter: 'blur(10px)',
        });
        gsap.set(b.halo, { xPercent: -50, yPercent: -50, scale: 0.65, opacity: 0.2 });
        /* The camera starts parked on the first installation, so a sector
           arrives already looking at something rather than panning onto it. */
        /* The camera starts parked on the first service, so a sector arrives
           already looking at something rather than panning onto it. */
        gsap.set(b.deep, { x: T.stations[0].camera * DEPTH.deep });
        gsap.set(b.far, { x: T.stations[0].camera * DEPTH.far });
        gsap.set(b.mid, { x: T.stations[0].camera * DEPTH.mid });
        gsap.set(b.near, { x: T.stations[0].camera * DEPTH.near });
        /* The renders are bottom-centred on the floor, and that has to be
           GSAP's `xPercent`/`yPercent` rather than a CSS transform: GSAP
           animates `y` on these, and a CSS `translate` on the same element is
           simply overwritten the first time it does. xPercent/yPercent it
           composes with. */
        gsap.set(b.stations, { autoAlpha: 0, y: LIFT_IN, transformOrigin: '50% 100%' });
        gsap.set(b.caps, { autoAlpha: 0, y: LIFT_IN * 0.5 });

        gsap.set([b.pool, b.cross, b.socket], { autoAlpha: 0 });
        gsap.set(b.pool, { svgOrigin: bayOrigin, scale: 0.8 });
        gsap.set(b.shock, { svgOrigin: bayOrigin, scale: 0.35, autoAlpha: 0 });

        gsap.set(b.feed, { strokeDashoffset: T.feedLen });
        gsap.set(b.plug, { autoAlpha: 0, scale: 0.4, svgOrigin: `${T.connector.x} ${T.connector.y}` });
        // Sector 01's scenery is the one standing there when the section pins.
        gsap.set(b.scene, { autoAlpha: b.el.dataset.chapter === '0' ? 1 : 0 });
        gsap.set(b.lamps, { opacity: 0 });
      });

      /* ── Reduced motion ────────────────────────────────────────────────────
         The story still gets told, as discrete states rather than a scrubbed
         performance: each sector shows its finished picture and scrolling
         moves between them. Nothing animates on a scroll frame. */
      if (reduced) {
        bundles.forEach(b => {
          gsap.set(b.depth, { y: 0, scale: 1 });
          gsap.set([b.lBay, b.lNet, b.lWork], { y: 0, scale: 1 });
          gsap.set(b.lamps, { opacity: 1 });
          gsap.set(b.core, { scaleX: 1, scaleY: 1, y: 0, autoAlpha: 1, filter: 'none' });
          gsap.set(b.halo, { scale: 1.1, opacity: 0.45 });
          gsap.set([b.pool, b.cross, b.socket], { autoAlpha: 1 });
          gsap.set(b.pool, { scale: 1 });
          gsap.set(b.feed, { strokeDashoffset: 0 });
          gsap.set(b.plug, { autoAlpha: 1, scale: 1 });
          gsap.set(b.stations, { autoAlpha: 1, x: 0, y: 0, scale: 1 });
          gsap.set(b.caps, { autoAlpha: 1, y: 0, scale: 1 });
        });

        ScrollTrigger.create({
          trigger: root,
          start: 'top top',
          end: 'bottom bottom',
          pin: stage,
          pinSpacing: false,
          invalidateOnRefresh: true,
          onUpdate: self => {
            const time = self.progress * total;
            const c = chapterAt(time);
            const s = CHAPTERS[c].services.length - 1;
            paintFill(self.progress);
            paintAccent(time);
            if (c === lastState.chapter) return;
            lastState.chapter = c;
            lastState.service = s;
            lastState.seated = true;
            lastState.phase = 'live';
            bundles.forEach((b, bc) => {
              gsap.set(b.el, { autoAlpha: bc === c ? 1 : 0 });
              gsap.set(b.scene, { autoAlpha: bc === c ? 1 : 0 });
            });
            applyActive(c, s, true);
            setActive({ chapter: c, service: s, seated: true, phase: 'live' });
          },
        });
        return;
      }

      /* ── Full choreography ─────────────────────────────────────────────────
         Built first, attached to scroll last, so nothing can fire an onUpdate
         against a half-built timeline. */
      const tl = gsap.timeline({ paused: true, defaults: { immediateRender: false } });

      bundles.forEach((b, c) => {
        const T = b.trace;
        const M = marks[c];
        const t0 = starts[c];
        const n = T.stations.length;
        const isFirst = c === 0;
        const isLast = c === CHAPTERS.length - 1;

        const seatAt = t0 + M.seatAt;
        const seatDur = M.chargeAt - M.seatAt;
        const chargeAt = t0 + M.chargeAt;
        const chargeDur = M.deliverAt - M.chargeAt;
        const deliverAt = t0 + M.deliverAt;
        const deliverDur = M.deliverDur;
        const liftAt = t0 + M.liftAt;
        const liftDur = M.liftDur;
        const endAt = t0 + M.span;

        /* ── APPROACH: the bay wakes and the sector rises into frame ────────
           The sector is not on screen yet as anything but a landing pad. That
           is the point: the reader is watching an empty bay open while the arm
           carries the board down to it, so the arrival is something they are
           waiting for rather than something that merely happened. */
        const wakeBay = (at, dur, ease) => {
          tl.fromTo(b.pool,
            { autoAlpha: 0, scale: 0.8 },
            { autoAlpha: 0.6, scale: 1, duration: dur, ease }, at);
          tl.fromTo(b.cross,
            { autoAlpha: 0 }, { autoAlpha: 0.45, duration: dur * 0.7, ease }, at);
          tl.fromTo(b.socket,
            { autoAlpha: 0 }, { autoAlpha: 0.55, duration: dur * 0.7, ease }, at + dur * 0.15);
        };

        if (isFirst) {
          /* The first sector has no predecessor to hand over from, and the
             section is not pinned yet while it is being approached — so its
             bay is opened by the section entering the viewport instead. By the
             time the stage pins, sector 01's bay is already standing there
             waiting for the board, rather than the reader pinning to an empty
             stage. */
          gsap.set(b.el, { autoAlpha: 1 });
          const intro = {
            trigger: root, start: 'top 92%', end: 'top top', scrub: 1,
          };
          gsap.fromTo(b.lBay, { y: 300, scale: 0.9 },
            { y: 0, scale: 1, ease: 'power2.out', scrollTrigger: intro });
          gsap.fromTo(b.lNet, { y: 400, scale: 0.88 },
            { y: 0, scale: 1, ease: 'power2.out', scrollTrigger: intro });
          gsap.fromTo(b.lWork, { y: 350 },
            { y: 0, ease: 'power2.out', scrollTrigger: intro });
          gsap.fromTo(b.pool,
            { autoAlpha: 0, scale: 0.8 },
            { autoAlpha: 0.6, scale: 1, ease: 'power2.out', scrollTrigger: intro });
          gsap.fromTo(b.cross,
            { autoAlpha: 0 }, { autoAlpha: 0.45, ease: 'power2.out', scrollTrigger: intro });
          gsap.fromTo(b.socket,
            { autoAlpha: 0 }, { autoAlpha: 0.55, ease: 'power2.out', scrollTrigger: intro });
        } else {
          /* Every other sector rises THROUGH the one it is replacing. The two
             overlap by RISE_LEAD, and they move in opposite directions — this
             one up from below and smaller than life, the outgoing one down and
             past the camera — which is what makes the seam read as flying into
             the next sector rather than as a cross-fade between two slides. */
          const riseAt = t0 - RISE_LEAD;
          tl.fromTo(b.el,
            { autoAlpha: 0 },
            { autoAlpha: 1, duration: RISE * 0.45, ease: 'power2.out' }, riseAt);

          /* Three distances, staggered — the bay closest and first, then the
             network it will feed, then the work itself furthest back. They all
             travel UP, because everything in this world does; the only thing
             that separates them is how far they came and when they set off.

             The offsets are large (300-620 units on a stage around 500 tall)
             for the reason the reference's are: a layer that enters from just
             off the edge reads as a slide-in, and one that comes up through
             half a screen reads as distance. */
          tl.fromTo(b.lBay,
            { y: 470, scale: 0.88 },
            { y: 0, scale: 1, duration: RISE, ease: 'power2.out' }, riseAt);
          tl.fromTo(b.lNet,
            { y: 620, scale: 0.84 },
            { y: 0, scale: 1, duration: RISE * 1.04, ease: 'power2.out' },
            riseAt + RISE * 0.07);
          tl.fromTo(b.lWork,
            { y: 545, scale: 0.9 },
            { y: 0, scale: 1, duration: RISE * 1.02, ease: 'power2.out' },
            riseAt + RISE * 0.04);

          wakeBay(riseAt + RISE * 0.30, RISE * 0.58, 'power2.out');

          /* The place changes with the sector. Cross-faded rather than flown,
             because the scenery is already travelling under the world's own
             parallax — moving it a second time would fight that, and a
             skyline that slides independently of its own ground reads as a
             mistake rather than as depth. */
          tl.fromTo(b.scene,
            { autoAlpha: 0 },
            { autoAlpha: 1, duration: RISE * 0.7, ease: 'power2.out' },
            riseAt + RISE * 0.1);
        }

        /* ── SEAT: the board is set down ───────────────────────────────────
           The arm reaches this exact moment on its own scroll map, off the
           fraction published in `publishArmSync`. Everything here is the
           consequence of a landing, not an entrance: the bay flares, the
           shock ring goes out, and the sector's core inflates out of it. */
        tl.fromTo(b.shock,
          { scale: 0.3, autoAlpha: 0.9, strokeWidth: 2.6 },
          { scale: 3.2, autoAlpha: 0, strokeWidth: 0.4, duration: seatDur * 1.5, ease: 'power3.out' },
          seatAt);

        tl.fromTo(b.pool,
          { scale: 1, autoAlpha: 0.6 },
          { scale: 1.16, autoAlpha: 1, duration: seatDur * 0.35, ease: 'power2.out' },
          seatAt);
        tl.fromTo(b.pool,
          { scale: 1.16, autoAlpha: 1 },
          { scale: 1.04, autoAlpha: 0.9, duration: seatDur * 0.65, ease: 'power2.out' },
          seatAt + seatDur * 0.35);

        tl.fromTo(b.socket,
          { autoAlpha: 0.55 }, { autoAlpha: 1, duration: seatDur * 0.4 }, seatAt);
        tl.fromTo(b.cross,
          { autoAlpha: 0.45 }, { autoAlpha: 0.9, duration: seatDur * 0.4 }, seatAt);

        // The sector boots out of the board: flat on the bay, then inflating.
        tl.fromTo(b.core,
          { scaleX: 0.55, scaleY: 0.06, y: 20, autoAlpha: 0, filter: 'blur(10px)' },
          {
            scaleX: 1, scaleY: 1, y: 0, autoAlpha: 1, filter: 'blur(0px)',
            duration: seatDur * 1.35, ease: 'back.out(1.6)',
          },
          seatAt + seatDur * 0.12);

        /* ── CHARGE: the board powers the site ─────────────────────────────
           The feed runs out to the connector, the plug seats, and the sector
           core takes its charge. There is no bus to draw any more — what the
           feed is powering is whatever installation is standing at the viewing
           position, and the camera has not gone anywhere yet. */
        tl.fromTo(b.feed,
          { strokeDashoffset: T.feedLen },
          { strokeDashoffset: 0, duration: chargeDur * 0.42, ease: 'power2.out' },
          chargeAt);

        tl.fromTo(b.plug,
          { autoAlpha: 0, scale: 0.4 },
          { autoAlpha: 1, scale: 1, duration: chargeDur * 0.2, ease: 'back.out(3)' },
          chargeAt + chargeDur * 0.36);

        tl.fromTo(b.halo,
          { scale: 0.65, opacity: 0.2 },
          { scale: 1.55, opacity: 0.9, duration: chargeDur * 0.34, ease: 'power2.in' },
          chargeAt + chargeDur * 0.12);
        tl.fromTo(b.halo,
          { scale: 1.55, opacity: 0.9 },
          { scale: 1.08, opacity: 0.45, duration: chargeDur * 0.46, ease: 'power3.out' },
          chargeAt + chargeDur * 0.46);

        /* The first installation is already at the viewing position — the
           camera was parked on it when the sector rose — so it stands up
           rather than being travelled to. */
        tl.fromTo(b.stations[0],
          { autoAlpha: 0, y: LIFT_IN },
          { autoAlpha: 1, y: 0, duration: chargeDur * 0.55, ease: 'power2.out' },
          chargeAt + chargeDur * 0.42);
        tl.fromTo(b.caps[0],
          { autoAlpha: 0, y: LIFT_IN, scale: 0.94 },
          {
            autoAlpha: 1, y: 0, scale: 1,
            duration: chargeDur * 0.66, ease: 'power2.out',
          },
          chargeAt + chargeDur * 0.40);


        /* ── TRAVEL: along the row, one service at a time ─────────────────
           The camera moves and the sector's services go past it. Each gets a
           slot: the first 58% is the move, the rest is the arrival and the
           hold.

           Three strips at three rates is the whole of the parallax — the
           renders track the camera exactly, the near props cross the entire
           frame between one service and the next, and the sector's place in
           the background creeps along behind on its own slow drift. Same rule
           as the reference's hills, turned on its side.

           `power2.inOut` on the move plus a real pause afterwards is what makes
           it read as a vehicle stopping at each one rather than a continuous
           pan that happens to have things in it. */
        const slot = deliverDur / n;
        const travel = slot * 0.58;

        for (let i = 1; i < n; i++) {
          const at = deliverAt + i * slot - travel;
          const from = T.stations[i - 1].camera;
          const to = T.stations[i].camera;

          [['deep', b.deep], ['far', b.far], ['mid', b.mid],
           ['near', b.near]].forEach(([k, targets]) => {
            tl.fromTo(targets,
              { x: from * DEPTH[k] },
              { x: to * DEPTH[k], duration: travel, ease: 'power2.inOut' },
              at);
          });

          /* The render is already standing when the camera gets there — it
             settles as it comes into frame, not after the camera has stopped,
             which is the difference between arriving somewhere and watching
             something be assembled for your benefit. */
          tl.fromTo(b.caps[i],
            { autoAlpha: 0, y: LIFT_IN, scale: 0.94 },
            {
              autoAlpha: 1, y: 0, scale: 1,
              duration: travel * 0.72, ease: 'power2.out',
            },
            at + travel * 0.20);
          tl.fromTo(b.stations[i],
            { autoAlpha: 0, y: LIFT_IN },
            { autoAlpha: 1, y: 0, duration: travel * 0.66, ease: 'power2.out' },
            at + travel * 0.22);

          /* The one behind dims as it leaves, so the frame is never a row of
             equally-lit objects — you are at one place at a time. */
          tl.fromTo(b.stations[i - 1],
            { autoAlpha: 1 },
            { autoAlpha: 0.26, duration: travel * 0.6, ease: 'power1.in' },
            at + travel * 0.2);
          tl.fromTo(b.caps[i - 1],
            { autoAlpha: 1 },
            { autoAlpha: 0, duration: travel * 0.4, ease: 'power1.in' },
            at + travel * 0.1);
        }


        /* Where each service becomes the one the story is telling: as the
           camera settles on it. */
        for (let i = 0; i < n; i++) {
          landings[c][i] = deliverAt + i * slot - travel * 0.45;
        }

        /* ── LIFT: the arm takes the board back ────────────────────────────
           The last sector never lifts — the finished picture is where the
           section should leave the reader — so it holds through its tail and
           the arm stays standing over it. */
        if (isLast) return;

        /* The site goes dark from the far end back toward the board — the
           board is being pulled out from under it, so the thing furthest from
           the board loses power first. */
        const retractSlot = (liftDur * 0.34) / n;
        for (let i = 0; i < n; i++) {
          const at = liftAt + (n - 1 - i) * retractSlot;
          tl.fromTo([b.stations[i], b.caps[i]],
            { autoAlpha: i === n - 1 ? 1 : 0.26 },
            { autoAlpha: 0, duration: liftDur * 0.22, ease: 'power2.in' },
            at);
        }
        tl.fromTo(b.plug,
          { autoAlpha: 1, scale: 1 },
          { autoAlpha: 0, scale: 0.4, duration: liftDur * 0.14, ease: 'power2.in' },
          liftAt + liftDur * 0.30);
        tl.fromTo(b.feed,
          { strokeDashoffset: 0 },
          { strokeDashoffset: T.feedLen, duration: liftDur * 0.22, ease: 'power2.in' },
          liftAt + liftDur * 0.34);

        // The core folds back down into the bay it grew out of — the seat,
        // played backwards, which is exactly what lifting the board is.
        tl.fromTo(b.halo,
          { scale: 1.08, opacity: 0.45 },
          { scale: 0.6, opacity: 0, duration: liftDur * 0.26, ease: 'power2.in' },
          liftAt + liftDur * 0.34);
        tl.fromTo(b.core,
          { scaleX: 1, scaleY: 1, y: 0, autoAlpha: 1, filter: 'blur(0px)' },
          {
            scaleX: 0.6, scaleY: 0.08, y: 18, autoAlpha: 0, filter: 'blur(8px)',
            duration: liftDur * 0.30, ease: 'power2.in',
          },
          liftAt + liftDur * 0.40);

        // The bay lets go: the pool dims, the pad is empty.
        tl.fromTo([b.pool, b.cross, b.socket],
          { autoAlpha: 1 },
          { autoAlpha: 0, duration: liftDur * 0.26, ease: 'power2.in' },
          liftAt + liftDur * 0.52);

        // The place goes with it.
        tl.fromTo(b.scene,
          { autoAlpha: 1 },
          { autoAlpha: 0, duration: liftDur * 0.5, ease: 'power2.in' },
          liftAt + liftDur * 0.5);

        /* ── EXIT: the sector flies up and past the lens ───────────────────
           UP, and growing — not down, and not shrinking. The camera is rising
           through this world and everything in it leaves over the top of the
           frame: the sector that has been told, and every band of the world
           behind it. The next sector is rising from below at the same moment,
           and because both are travelling the same way the seam reads as
           moving THROUGH one into the next rather than as a shuffle.

           540 units up and 1.45x is a long way past the lens on a stage this
           size. That is the point — the reference throws its outgoing scene a
           whole frame height and half again its size, and anything less looks
           like a card being dismissed. */
        const exitAt = liftAt + liftDur * 0.44;
        const exitDur = (endAt + RISE_LEAD * 0.7) - exitAt;
        tl.fromTo(b.depth,
          { y: 0, scale: 1 },
          { y: -540, scale: 1.45, duration: exitDur, ease: 'power2.in' },
          exitAt);
        tl.fromTo(b.el,
          { autoAlpha: 1 },
          { autoAlpha: 0, duration: exitDur * 0.72, ease: 'power2.in' },
          exitAt + exitDur * 0.28);

        /* The world goes with it. Each band is whipped up by an amount set by
           how near it is — the bottom board edge crosses half the frame, the
           far skyline barely notices — and then brought back for the sector
           that is arriving. It has to come BACK: a surge that only travelled
           would have to snap to zero at the next hand-off, and a scrubbed
           timeline shows every snap. */
        const surge = [[world.motes, -104]];
        surge.forEach(([band, dy]) => {
          if (!band.surge) return;
          tl.fromTo(band.surge,
            { y: 0 }, { y: dy, duration: exitDur * 0.62, ease: 'power2.in' }, exitAt);
          tl.fromTo(band.surge,
            { y: dy }, { y: 0, duration: RISE * 0.8, ease: 'power2.out' },
            exitAt + exitDur * 0.62);
        });
      });

      /* ── the readout ───────────────────────────────────────────────────────
         Hung on the TIMELINE's tick rather than the ScrollTrigger's, and that
         distinction is the whole of it.

         ScrollTrigger fires `onUpdate` when the SCROLL moves. Under `scrub`
         the playhead does not arrive with the scroll — it keeps easing toward
         it for another second after the reader has stopped — so the last
         `onUpdate` of a gesture reports a playhead that is still mid-glide,
         and then nothing fires again to correct it. Read the clock there and
         the panel settles on whatever sector happened to be under the playhead
         at the instant scrolling stopped, which after a long flick is the
         wrong one entirely.

         The timeline's own `onUpdate` fires on every frame the playhead moves,
         catch-up included, so the copy is always describing the frame that is
         actually on screen. */
      const readout = () => {
        const time = clock.t;
        paintAccent(time);

        const c = chapterAt(time);
        const M = marks[c];
        const t0 = starts[c];

        /* Three moments split a sector into four states:

             inbound   the arm is still carrying the board down to the bay.
             live      the board is seated and this sector is running on it.
             handover  the arm has closed on the board again and is taking it
                       to the next sector, while this one's services retract.

           `handover` is the one that is easy to forget, and leaving it out is
           what had the panel announcing an inbound board over a sector that
           was in the middle of finishing. The last sector never enters it —
           it keeps the board. */
        const phase = time < t0 + M.seatAt ? 'inbound'
          : (!M.isLast && time >= t0 + M.liftAt) ? 'handover'
            : 'live';
        const seated = phase === 'live';

        const mk = landings[c];
        let sv = -1;
        if (seated) {
          for (let i = 0; i < mk.length; i++) {
            if (time >= mk[i]) sv = i;
          }
        }

        if (c !== lastState.chapter || sv !== lastState.service
            || seated !== lastState.seated || phase !== lastState.phase) {
          lastState.chapter = c;
          lastState.service = sv;
          lastState.seated = seated;
          lastState.phase = phase;
          applyActive(c, sv, seated);
          setActive({ chapter: c, service: sv, seated, phase });
        }
      };
      tl.eventCallback('onUpdate', readout);

      /* ── the clock ─────────────────────────────────────────────────────────
         One tween, spanning the whole schedule, animating one number.

         It exists because a GSAP timeline is exactly as long as its last tween
         ends — and the last sector deliberately has no LIFT, it holds its
         finished picture through a tail instead. So the timeline stopped short
         of the scroll the section had reserved for it, ScrollTrigger mapped
         the full scroll range onto that shorter timeline, and every beat ran
         early against the schedule the readout and the ROBOT ARM were both
         using. The arm would have reached for a board the bay had not been
         told to expect yet.

         `clock.t` fixes both halves of that at once: the tween's own length
         pins the timeline's duration to `total`, and its value is story time,
         read straight out rather than inferred from a progress ratio that
         assumes the two are equal. Nothing downstream has to know how long the
         timeline happens to be. */
      tl.fromTo(clock, { t: 0 }, { t: total, duration: total, ease: 'none' }, 0);

      ScrollTrigger.create({
        trigger: root,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 1.1,                     // turns a flick into a glide
        animation: tl,
        pin: stage,
        pinSpacing: false,              // .zx-story already reserves the room
        anticipatePin: 1,
        invalidateOnRefresh: true,
        /* The robot arm's cues, in the only coordinates that cannot drift from
           this timeline: this trigger's own resolved scroll range. Republished
           on every refresh, which covers every resize and every change of
           height above this section. */
        onRefresh: self => publishArmSync(schedule, self.start, self.end),
        // Only the scroll-owned clock. Everything that describes what is being
        // shown is on the timeline's tick — see `readout` above.
        onUpdate: self => paintFill(self.progress),
      });

      /* ── The world's own travel ────────────────────────────────────────────
         Every band drifts the whole length of the section, at its own rate, on
         its own trigger and its own deliberately LAGGING scrub. The lag is
         load-bearing: the story is scrubbed at 1.1 and the world at 4, so the
         distance trails the work the way distance does, and the stage stops
         reading as flat.

         These are the slow rates. The fast ones are the per-hand-off surges
         built above, which is why the two live on different elements. */
      /* Rates, far to near. The ridges barely move, the air moves a lot —
         same rule as everywhere else in this section, and the reason the
         background reads as distance rather than as a painted flat. */
      const drift = [
        [W('place'), { x: -22 }, 6],
        [W('far'), { x: -34 }, 6],
        [W('mid'), { x: -96 }, 5],
        [W('near'), { x: -210 }, 4],
        [world.motes, { y: -300, x: -130 }, 5],
      ];
      drift.forEach(([band, to, scrub]) => {
        if (!band.drift) return;
        const t = gsap.timeline();
        ScrollTrigger.create({
          animation: t, trigger: root, start: 'top top', end: 'bottom bottom',
          scrub, invalidateOnRefresh: true,
        });
        t.fromTo(band.drift, { x: 0, y: 0 }, { ...to, ease: 'none' }, 0);
      });

      /* ── The background ────────────────────────────────────────────────────
         Not a backdrop — the largest thing on the stage, and the thing that
         says which sector you are in before a single word has been read.

         Two motions. The light source crawls across the horizon for the whole
         section on its own slow, lagging trigger, which is the reference's sun
         and is what keeps a twelve-viewport section from ever looking static.
         And on the master timeline every surface repaints from the outgoing
         sector's ramp to the incoming one as the board is carried across.

         Every stop is tweened, not just the hue: sky, glow, all three ridges
         and both ends of the floor. Repainting one and not the others is what
         produces a warm sky over a cold floor. */
      const skyDrift = gsap.timeline();
      ScrollTrigger.create({
        animation: skyDrift, trigger: root, start: 'top top', end: 'bottom bottom',
        scrub: 5, invalidateOnRefresh: true,
      });
      if (bg.sun) {
        skyDrift.fromTo(bg.sun,
          { attr: { cx: dims.stageW * 0.30 } },
          { attr: { cx: dims.stageW * 0.74 }, ease: 'none' }, 0);
      }

      CHAPTERS.forEach((ch, c) => {
        if (c === 0) return;
        const A = ramp(CHAPTERS[c - 1].accent);
        const B = ramp(ch.accent);
        const at = starts[c] - RISE_LEAD;
        const dur = marks[c].chargeAt + RISE_LEAD;

        const pairs = [];
        ['sky0', 'sky1', 'sky2', 'sky3'].forEach((k, i) => {
          if (bg.sky[i]) pairs.push([bg.sky[i], 'stop-color', k]);
        });
        ['glow0', 'glow1'].forEach((k, i) => {
          if (bg.glow[i]) pairs.push([bg.glow[i], 'stop-color', k]);
        });
        ['ground0', 'ground1'].forEach((k, i) => {
          if (bg.ground[i]) pairs.push([bg.ground[i], 'stop-color', k]);
        });
        ['far', 'mid', 'near'].forEach((k, i) => {
          if (bg.bands[i]) pairs.push([bg.bands[i], 'fill', k]);
        });

        pairs.forEach(([el, attr, key]) => {
          tl.fromTo(el,
            { attr: { [attr]: A[key] } },
            { attr: { [attr]: B[key] }, duration: dur, ease: 'power1.inOut' },
            at);
        });

        /* The place changes with the sector, and it is the single clearest
           statement the section makes about which sector you are in — a
           hospital gives way to a substation. Cross-faded over the same window
           as the light, so the room and what is standing in it arrive
           together. */
      });

      /* Only the sector the reader is in has its place standing. */

      applyActive(0, -1, false);
      paintFill(0);
      paintAccent(0);
    }, rootRef);

    return () => ctx.revert();
  /* `dims.stageW` is in here because the sun's drift is tweened across the
     stage's real width — a resize has to rebuild that tween, not keep animating
     to the old edge. The rest of `dims` reaches this effect through `layouts`,
     which is derived from it. */
  }, [isMobile, reduced, layouts, schedule, marks, starts, total, dims.stageW]);

  if (isMobile) return <MobileStory />;

  const chapter = CHAPTERS[active.chapter] || CHAPTERS[0];
  const nextChapter = CHAPTERS[active.chapter + 1] || null;
  const service = active.seated && active.service >= 0
    ? chapter.services[active.service]
    : null;

  return (
    <section
      ref={rootRef}
      className="zx-story"
      aria-label="Industries and the services we deliver in each"
      style={{ height: `calc(100vh + ${total}vh)`, '--zx-live-accent': CHAPTERS[0].accent }}
    >
      <div className="zx-stage" ref={stageRef}>
        <WorldBack
          width={dims.stageW}
          height={dims.stageH}
          horizon={dims.top + (layouts[0] ? layouts[0].groundY : 0)}
        />

        <header className="zx-head">
          <span className="zx-head__eyebrow">Our Services</span>
          <h2 className="zx-head__title">
            End-to-End Engineering, <span>Everything</span> Under One Roof
          </h2>
          <p className="zx-head__sub">
            {CHAPTER_COUNT} industries. {TOTAL_SERVICES} purpose-built services.
            One board, carried into every sector it powers.
          </p>
          {/* Which sector you are in — named, and named ALWAYS.

              It was only in the panel before, in eyebrow type, and the panel
              cross-fades at every hand-off: for the whole of a sector change
              there was nothing on the screen that said where you were. A
              section whose entire subject is four industries has to name the
              one you are looking at, and it costs nothing to name the other
              three at the same time. Now the structure is legible from the
              first frame — four sectors, this is the one you are in, that is
              how many are left. */}
          <nav className="zx-head__sectors" aria-label="Sectors">
            {CHAPTERS.map((c, i) => (
              <span
                key={c.id}
                className="zx-head__sector"
                data-live={i === active.chapter ? 'true' : 'false'}
                data-past={i < active.chapter ? 'true' : 'false'}
                style={{ '--c': c.accent }}
              >
                <b className="zx-head__sectorNum">{c.id}</b>
                {c.short || c.name}
              </span>
            ))}
          </nav>

          <span className="zx-head__meter" aria-hidden="true">
            <span className="zx-head__meterFill" />
          </span>
        </header>

        <div className="zx-body">
          {/* The arm's lane. This used to be the 01-04 rail; its track, fill
              and numbered stops are gone from the UI, but the element itself
              stays because it is the column PcbJourney measures and stands
              the robot arm in. Nothing is drawn here — the arm is. */}
          <div className="zx-rail zx-arm-lane" ref={railRef} aria-hidden="true" />

          <div className="zx-canvas" ref={canvasRef}>
            <StageVisual chapters={CHAPTERS} layouts={layouts} />
          </div>

          <StoryPanel
            chapter={chapter}
            nextChapter={nextChapter}
            service={service}
            phase={active.phase}
          />
        </div>

        <div className="zx-hint" aria-hidden="true">
          <span className="zx-hint__bar" />
          Scroll
        </div>
      </div>
    </section>
  );
}

/* ─── RIGHT PANEL ──────────────────────────────────────────────────────────
   Three layers that cross-fade, one per state the stage can be in:

     INBOUND   the arm is still carrying the board down to this sector's bay.
               The sector is named but not yet live — the copy is deliberately
               a manifest rather than a pitch, because nothing has happened yet.
     SECTOR    the board is seated and the bus is drawing.
     SERVICE   whichever service the packet has most recently delivered.
     HANDOVER  the arm has taken the board back and this sector is retracting.
               It reads as a summary and a pointer to where the board is going,
               because that is exactly what the stage is showing.

   The service layer is keyed on the slug so each new service animates in
   rather than having its text swapped underneath the reader. React owns this
   entirely — GSAP never touches the panel — so the two can never fight over a
   property here either. */
function StoryPanel({ chapter, nextChapter, service, phase }) {
  const Glyph = INDUSTRY_ICONS[chapter.id];
  const state = phase === 'handover' ? 'handover'
    : service ? 'service'
      : phase === 'live' ? 'sector'
        : 'inbound';

  const glyph = (
    <span
      className="zx-panel__glyph"
      style={{
        background: `${chapter.accent}18`,
        border: `1.5px solid ${chapter.accent}40`,
        boxShadow: `0 4px 18px ${chapter.accent}30`,
      }}
    >
      {chapter.img
        ? <img src={chapter.img} alt="" style={{ width: 34, height: 34, objectFit: 'contain' }} />
        : Glyph ? <Glyph c={chapter.accent} /> : null}
    </span>
  );

  return (
    <aside className="zx-panel" data-state={state}>
      {/* ── INBOUND ── */}
      <div className="zx-panel__layer" data-show={state === 'inbound' ? 'true' : 'false'}>
        <div className="zx-panel__swap" key={`in-${chapter.id}`}>
          <span className="zx-panel__status" style={{ color: chapter.accent }}>
            <span className="zx-panel__pulse" style={{ background: chapter.accent }} />
            Board inbound
          </span>
          <span className="zx-panel__bignum" style={{ color: `${chapter.accent}22` }}>
            {chapter.id}
          </span>
          <h3 className="zx-panel__title">{chapter.name}</h3>
          <p className="zx-panel__body">
            The arm is setting the board into this sector&rsquo;s bay.
            On contact, {chapter.services.length} services come online.
          </p>
          <div className="zx-panel__tags">
            {chapter.tags.map(tag => (
              <span
                key={tag}
                className="zx-panel__tag"
                style={{
                  color: chapter.accent,
                  background: `${chapter.accent}14`,
                  borderColor: `${chapter.accent}40`,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── SECTOR ── */}
      <div className="zx-panel__layer" data-show={state === 'sector' ? 'true' : 'false'}>
        <div className="zx-panel__swap" key={`sec-${chapter.id}`}>
          <span className="zx-panel__rule" style={{ background: chapter.accent }} />
          <div className="zx-panel__head">
            {glyph}
            <span className="zx-panel__eyebrow" style={{ color: chapter.accent }}>
              Sector {chapter.id} / {String(CHAPTER_COUNT).padStart(2, '0')}
            </span>
          </div>

          <h3 className="zx-panel__title">{chapter.name}</h3>
          <p className="zx-panel__body">{chapter.desc}</p>

          <div className="zx-panel__tags">
            {chapter.tags.map(tag => (
              <span
                key={tag}
                className="zx-panel__tag"
                style={{
                  color: chapter.accent,
                  background: `${chapter.accent}14`,
                  borderColor: `${chapter.accent}40`,
                }}
              >
                {tag}
              </span>
            ))}
          </div>

          <Link
            className="zx-panel__cta"
            to={chapter.to}
            style={{ background: chapter.accent, boxShadow: `0 4px 16px ${chapter.accent}45` }}
          >
            Explore Industry <Arrow />
          </Link>

          <p className="zx-panel__count">
            {chapter.services.length} services in this sector
          </p>
        </div>
      </div>

      {/* ── HANDOVER ── */}
      <div className="zx-panel__layer" data-show={state === 'handover' ? 'true' : 'false'}>
        <div className="zx-panel__swap" key={`out-${chapter.id}`}>
          <span className="zx-panel__status" style={{ color: chapter.accent }}>
            <span className="zx-panel__tick" style={{ background: chapter.accent }} />
            Sector complete
          </span>
          <h3 className="zx-panel__title">{chapter.name}</h3>
          <p className="zx-panel__body">
            {chapter.services.length} services delivered on one board.
            {nextChapter
              ? ` The arm is lifting it across to ${nextChapter.short}.`
              : ''}
          </p>

          <Link
            className="zx-panel__cta"
            to={chapter.to}
            style={{ background: chapter.accent, boxShadow: `0 4px 16px ${chapter.accent}45` }}
          >
            Explore Industry <Arrow />
          </Link>

          {nextChapter && (
            <p className="zx-panel__count">
              Next &mdash; Sector {nextChapter.id} / {nextChapter.short}
            </p>
          )}
        </div>
      </div>

      {/* ── SERVICE ── */}
      <div className="zx-panel__layer" data-show={state === 'service' ? 'true' : 'false'}>
        {service && (
          <div className="zx-panel__swap" key={service.slug}>
            <span className="zx-panel__rule" style={{ background: chapter.accent }} />
            <div className="zx-panel__head">
              <span
                className="zx-panel__glyph"
                style={{
                  background: `${chapter.accent}14`,
                  border: `1.5px solid ${chapter.accent}38`,
                }}
              >
                <ServiceIcon slug={service.slug} color={chapter.accent} size={32} />
              </span>
              <span className="zx-panel__eyebrow" style={{ color: chapter.accent }}>
                {service.id} / {String(chapter.services.length).padStart(2, '0')}
                <span style={{ opacity: 0.55 }}>{chapter.short}</span>
              </span>
            </div>

            <h3 className="zx-panel__title">{service.title}</h3>
            <p className="zx-panel__body">{service.brief}</p>

            <ul className="zx-panel__list">
              {service.deliver.map((item, i) => (
                <li key={item} style={{ '--i': i }}>
                  <Tick color={chapter.accent} />
                  {item}
                </li>
              ))}
            </ul>

            <Link
              className="zx-panel__cta"
              to={service.to}
              style={{ background: chapter.accent, boxShadow: `0 4px 16px ${chapter.accent}45` }}
            >
              View Service <Arrow />
            </Link>
          </div>
        )}
      </div>
    </aside>
  );
}

function Arrow() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2 6h8M6.5 2.5L10 6l-3.5 3.5" stroke="currentColor" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Tick({ color }) {
  return (
    <svg width="15" height="15" viewBox="0 0 14 14" fill="none"
      style={{ marginTop: 3, flexShrink: 0 }} aria-hidden="true">
      <circle cx="7" cy="7" r="7" fill={`${color}1F`} />
      <path d="M4 7.2 L6 9.2 L10 4.8" stroke={color} strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
